import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyRadioModule } from '@angular/material/legacy-radio';
import {
  AlterationRequestPayload,
  DirectAlteration,
  DirectAlterationExpiration,
  DirectAlterationRequestReservationDetailsComponent,
  DirectAlterationReviewAndConfirmListComponent,
  DirectAlterationsService,
} from '@app/features/alteration-requests';
import { Reservation } from '@app/shared/interfaces/lib/reservation.interface';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { ModalFullComponent } from '@ui';
import { Logger } from '@utils';
import { Subject, switchMap } from 'rxjs';

enum Mode {
  Details,
  Expiration,
  Confirmation,
  Success,
}

@Component({
  standalone: true,
  selector: 'sbnb-dialog-request-direct-alteration',
  templateUrl: './dialog-request-direct-alteration.component.html',
  imports: [
    CommonModule,
    ModalFullComponent,
    MatLegacyRadioModule,
    DirectAlterationRequestReservationDetailsComponent,
    DirectAlterationReviewAndConfirmListComponent,
  ],
})
export class DialogSendDirectAlterationRequestComponent {
  private readonly dialogRef = inject(MatDialogRef<DialogSendDirectAlterationRequestComponent>);
  private readonly directAlterationsService = inject(DirectAlterationsService);
  private readonly destroyRef = inject(DestroyRef);
  readonly #segmentIoService = inject(SegmentIoService);
  data = inject<{ reservation: Reservation }>(MAT_DIALOG_DATA);

  private readonly quoteRequestSubject = new Subject<AlterationRequestPayload>();

  fetchingQuote = signal<boolean>(false);
  quoteError = signal<string | null>(null);

  submittingAlteration = signal<boolean>(false);

  // This is the object that needs to remain the source of truth - its what we always send to the backend
  // We'll never have one when we first open the dialog, we'll either have a reservation, or an alteration (details) from the backend (for an open alteration)
  readonly alterationRequest = signal<AlterationRequestPayload | null>(
    this.directAlterationsService.generateAlterationPayloadFromReservation(this.data.reservation)
  );

  readonly alterationDetails = signal<DirectAlteration | null>(null);

  Mode = Mode;
  readonly mode = signal<Mode>(Mode.Details);

  readonly primaryAction = computed(() => {
    return {
      label:
        this.mode() === Mode.Confirmation
          ? 'Confirm the alteration'
          : this.mode() === Mode.Success
            ? 'Done'
            : 'Continue',
      disabled: this.fetchingQuote() || (this.quoteError() && this.mode() === Mode.Details),
      loading: this.submittingAlteration(),
      onClick: () => this.next(),
    };
  });

  readonly secondaryAction = computed(() => {
    if (this.mode() === Mode.Success) {
      return null;
    }

    return {
      label: this.mode() === Mode.Details ? 'Cancel' : 'Back',
      disabled: this.fetchingQuote(),
      onClick: () => this.back(),
    };
  });

  readonly progressPercentage = computed(() => {
    if (this.mode() === Mode.Details) {
      return 30;
    }

    if (this.mode() === Mode.Expiration) {
      return 60;
    }

    if (this.mode() === Mode.Confirmation) {
      return 90;
    }

    return 0;
  });

  constructor() {
    this.#analyticsTrackDialogWasOpened();
    this.setupQuoteRequestStream();
    this.fetchQuote();
  }

  private setupQuoteRequestStream() {
    this.quoteRequestSubject
      .pipe(
        switchMap((alterationRequest) => {
          this.fetchingQuote.set(true);
          this.quoteError.set(null);

          if (!alterationRequest) {
            Logger.error('no alteration request');
            this.fetchingQuote.set(false);
            return [];
          }

          return this.directAlterationsService.fetchPriceQuote(this.data.reservation.uuid, alterationRequest);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: { quote: DirectAlteration | null; error: string | null }) => {
          this.fetchingQuote.set(false);

          if (res.quote) {
            this.alterationDetails.set(res.quote);
            this.quoteError.set(null);
          } else {
            this.quoteError.set(res.error);
          }
        },
        error: (error) => {
          this.quoteError.set(error?.error?.message ?? 'An error occurred generating a quote');
          this.fetchingQuote.set(false);
        },
      });
  }

  close(alterationWasSubmitted = false) {
    this.dialogRef.close(alterationWasSubmitted);
  }

  next() {
    switch (this.mode()) {
      case Mode.Details:
        this.mode.set(Mode.Expiration);
        break;
      case Mode.Expiration:
        this.mode.set(Mode.Confirmation);
        break;
      case Mode.Confirmation:
        this.submitAlteration();
        break;
      case Mode.Success:
        this.close();
        break;
    }
  }

  back() {
    switch (this.mode()) {
      case Mode.Details:
        this.close();
        break;
      case Mode.Expiration:
        this.mode.set(Mode.Details);
        break;
      case Mode.Confirmation:
        this.mode.set(Mode.Expiration);
        break;
    }
  }

  expirationRadioButtonClicked(event: Event, expiration: DirectAlterationExpiration) {
    event.stopPropagation();
    event.preventDefault();

    this.alterationRequest.update((alterationRequest) => {
      if (!alterationRequest) return alterationRequest;

      return {
        ...alterationRequest,
        expires_in: expiration,
      };
    });
  }

  fetchQuote() {
    const alterationRequest = this.alterationRequest();
    if (alterationRequest) {
      this.quoteRequestSubject.next(alterationRequest);
    }
  }

  submitAlteration() {
    this.submittingAlteration.set(true);

    this.directAlterationsService.submitAlteration(this.data.reservation.uuid, this.alterationRequest()).subscribe({
      next: () => {
        this.submittingAlteration.set(false);
        this.mode.set(Mode.Success);
      },
      error: () => {
        this.submittingAlteration.set(false);
      },
    });
  }

  #analyticsTrackDialogWasOpened() {
    this.#segmentIoService.track(SegmentEvent.DirectAlterationRequestDialogOpened, {
      reservationId: this.data.reservation.uuid,
    });
  }
}
